import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { checklistSubmissionApi } from 'src/Services/API';
import { ChecklistSubmissionDto, CreateChecklistSubmissionDto } from 'src/Services/API/generated';
import { trackEvent } from 'src/Services/Matomo';

export const useSubmitChecklist = (
  options?: UseMutationOptions<
    ChecklistSubmissionDto,
    unknown,
    { checklist: CreateChecklistSubmissionDto; checklistId: string }
  >
) => {
  const assetId = useCurrentAssetId();
  const queryClient = useQueryClient();

  return useMutation<
    ChecklistSubmissionDto,
    unknown,
    { checklist: CreateChecklistSubmissionDto; checklistId: string }
  >(
    ['submit-checklist', assetId],
    async ({
      checklist,
      checklistId,
    }: {
      checklist: CreateChecklistSubmissionDto;
      checklistId: string;
    }) => {
      if (!assetId) {
        throw new Error('No asset selected');
      }
      const response = await checklistSubmissionApi.checklistSubmissionsControllerCreate(
        assetId,
        checklistId,
        checklist
      );
      return response.data;
    },
    {
      ...options,
      onSuccess: (
        data: ChecklistSubmissionDto,
        variables: { checklist: CreateChecklistSubmissionDto; checklistId: string },
        context: unknown
      ) => {
        queryClient.invalidateQueries({ queryKey: ['fetch-checklists'] });
        queryClient.invalidateQueries({
          queryKey: ['fetch-checklist-submissions', variables.checklistId],
        });
        if (options?.onSuccess) {
          options.onSuccess(data, variables, context);
        }
        trackEvent('Checklist', 'submit');
      },
    }
  );
};
